/* global AbortSignal */
import { api } from "./client";

// Типы 1:1 к ответам backend `mp_finance/router.py` (`/mp-analytics/{ozon|wb}/*`) и
// `mp_finance/ozon_router.py` (только Ozon: поставки, кампании; расчёт — ozon_analytics/). Источник правды —
// docs/ozon-analytics.md. Суммы со знаком, как в витрине: выручка +, удержания −. Δ, доли и
// проценты считает фронт (pages/ozon-analytics/calc.ts). Клиент по площадке — mpAnalytics.ts.

/** Свежесть источников витрины WB по юрлицу: ISO дата/время или null (данных нет). */
export interface DataThrough {
  realization_op_date: string | null;
  realization_weekly_to: string | null;
  realization_daily_to: string | null;
  realization_insert_date: string | null;
  reports_loaded_at: string | null;
  storage_date: string | null;
  adverts_date: string | null;
  cards_snapshot_date: string | null;
  balance_snapshot_date: string | null;
  order_feed_loaded_at: string | null;
}

export interface MartState {
  built_at: string;
  date_from: string;
  date_to: string;
  /** свежесть по источникам на каждое юрлицо; ключи — WB (`DataThrough`). У Ozon ключи свои
   *  (accrual_date, balance_month, …) и UI их пока не показывает (`mp.ts` freshnessBySource) */
  data_through?: Record<string, DataThrough> | null;
}

interface Envelope {
  mart: MartState;
  from_cache: boolean;
}

export interface OzMeta extends Envelope {
  legal_names: string[];
  legal_ranges: { legal_name: string; date_from: string; date_to: string }[];
  /** `reference` — справочная группа (WB `spp`): в P&L не входит; `cogs` — группа
   *  «Себестоимость» (контракт v1.4): не рубли площадки, в удержания (`deductions`/`cost`) не
   *  входит, в /summary — только показателями `totals.*.cogs_*`/`expenses`; у прочих ключей нет */
  groups: { code: string; label: string; reference?: boolean; cogs?: boolean }[];
  dims: { code: string; label: string }[];
  max_period_days: number;
}

/** Общие параметры всех отчётных ручек. */
export interface OzFilters {
  date_from: string;
  date_to: string;
  cmp_from: string | null;
  cmp_to: string | null;
  legal: string | null;
  alloc: boolean;
}

export interface SummaryRow {
  grp: string;
  grp_label: string;
  item: string;
  cur: number;
  cmp: number | null;
  cur_qty: number;
  cmp_qty: number | null;
  /** строка справочной группы (WB `spp`): в итоги P&L (`totals`) не входит */
  reference?: boolean;
}

export interface SummaryGroup {
  grp: string;
  label: string;
  cur: number;
  cmp: number | null;
  /** справочная группа (WB `spp`): в итоги P&L (`totals`) не входит */
  reference?: boolean;
}

/** Показатели себестоимости (контракт mp_finance v1.4/v1.7) и схемы экранов «Выручка − Расходы =
 *  Маржа» (решение владельца 22.10.2026). null — период начат раньше `cogs.cogs_from`, цены не
 *  зафиксированы (`cogs.status = no_prices`) или у площадки себестоимости нет — не 0, не частичная
 *  сумма и не подмена удержаниями / маржой до себестоимости. Знак — как в витрине: расход −. */
export interface CogsMetrics {
  /** себестоимость производства проданного, ₽ с НДС */
  cogs_goods: number | null;
  /** склад производства, ₽ без НДС */
  cogs_wh: number | null;
  /** «Расходы» = удержания площадки + cogs_goods + cogs_wh (= маржа − выручка) */
  expenses: number | null;
  /** «Маржа» = выручка + expenses (= margin + cogs_goods + cogs_wh) */
  net_margin: number | null;
  /** % маржи от выручки (×100, 2 знака); выручки нет — null */
  net_margin_pct: number | null;
}

/** Себестоимость в ответе: есть ли у площадки, с какой даты, покрыт ли период. */
export interface CogsView {
  /** у площадки есть себестоимость (обе площадки — true) */
  supported: boolean;
  /** unsupported — у площадки себестоимости нет; no_prices — цены себестоимости не
   *  зафиксированы (`mp_cogs_state.prices_active = 0`): показатели null везде; ok — считается */
  status: "unsupported" | "no_prices" | "ok";
  /** дата начала себестоимости (ISO; dbt-переменная, при `no_prices` тоже заполнена) */
  cogs_from: string | null;
  /** снимок цен в портале (ISO дата-время) — «цены на …» */
  prices_exported_at: string | null;
  covered_cur: boolean;
  /** null — сравнения нет */
  covered_cmp: boolean | null;
}

/** Показатели себестоимости бэкенд отдаёт всегда (у Ozon — null); в типе необязательны, чтобы
 *  отсутствие поля читалось так же, как null («—» с подсказкой), а не падало. */
export interface Totals extends Partial<CogsMetrics> {
  revenue: number;
  deductions: number;
  /** выручка + удержания площадки, БЕЗ себестоимости: на экранах «Аналитики МП» не показывается
   *  (кроме «К перечислению» во вкладке «По дням») — маржа там `net_margin` */
  margin: number;
  qty: number;
}

export interface OzSummary extends Envelope {
  rows: SummaryRow[];
  groups: SummaryGroup[];
  totals: { cur: Totals; cmp: Totals | null };
  /** нет — «себестоимость: нет данных» */
  cogs?: CogsView;
}

export interface TreeMetrics extends Partial<CogsMetrics> {
  rev: number;
  /** удержания площадки (без себестоимости); «Расходы» узла — `expenses` */
  cost: number;
  qty: number;
}

export interface TreeFlags {
  /** все строки узла — «прямое» */
  direct_only: boolean;
  /** хоть одна строка узла — «прямое» */
  has_direct: boolean;
  has_alloc: boolean;
  has_common: boolean;
  /** узел «Группа статей» справочной группы (WB `spp`) — в P&L не входит */
  reference?: boolean;
  /** узел только из строк себестоимости (группа «Себестоимость», её статьи): выручки и
   *  удержаний площадки нет, суммы — в `cogs_goods`/`cogs_wh` */
  cogs?: boolean;
}

/** Карточка товара у узлов «Артикул» и «SKU»/«nmID» (у остальных измерений — null). */
export interface TreeProduct {
  /** артикул продавца (vendor_code витрины) */
  offer_id: string | null;
  /** поле старой витрины Ozon — теперь всегда null; SKU — `mp_sku` */
  sku: number | null;
  sku_count: number;
  name_1c: string | null;
  /** поле старой витрины Ozon — теперь всегда null; название площадки — `name_mp` */
  name_ozon: string | null;
  code_1c: string | null;
  image_url: string | null;
  /** товар на площадке (текст): SKU Ozon / nmID WB — по нему ссылка на витрину МП */
  mp_sku: string | null;
  /** название товара на площадке */
  name_mp: string | null;
  /** сопоставление товара с 1С (справочник витрины) */
  mapping_status?: "ok" | "ambiguous" | "unmapped" | null;
  /** ключа узла нет в справочнике витрины — текст пометки (он же подпись узла) */
  dim_missing?: string | null;
}

export interface TreeRow {
  key: string;
  label: string;
  /** у «Артикула»/«SKU» — название 1С, иначе Ozon */
  sublabel: string | null;
  product: TreeProduct | null;
  cur: TreeMetrics;
  cmp: TreeMetrics | null;
  flags: TreeFlags;
  has_children: boolean;
}

export interface PathStep {
  dim: string;
  key: string;
}

export type TreeSort = "abs" | "rev" | "cost" | "delta";

export interface TreeRequest extends OzFilters {
  levels: string[];
  path: PathStep[];
  search: string | null;
  limit: number;
  offset: number;
  sort: TreeSort;
  /** только объекты этого вида (`obj_kind`), когда раскрываемый уровень — `obj` */
  obj_kind?: string | null;
}

export interface OzTree extends Envelope {
  dim: string;
  depth: number;
  total_count: number;
  rows: TreeRow[];
  cogs?: CogsView;
  /** сортировка `abs`/`cost`/`delta` шла без себестоимости (период / период сравнения раньше
   *  `cogs_from`, цен нет) — пометка рядом с «Сортировкой»; null — с себестоимостью или не нужна */
  sort_note?: string | null;
}

export type Grain = "day" | "week" | "month";

export interface TrendBucket extends Partial<CogsMetrics> {
  bucket: string;
  /** без группы себестоимости (она — в `cogs_*`) */
  by_grp: Record<string, number>;
  rev: number;
  cost: number;
  margin: number;
  qty: number;
}

export interface OzTrend extends Envelope {
  grain: Grain;
  /** эхо среза: группа и статья P&L (null — все) */
  grp: string | null;
  item: string | null;
  /** только корзины с начислениями */
  cur: TrendBucket[];
  cmp: TrendBucket[] | null;
  /** ISO-начала ВСЕХ корзин периода, включая пустые (ось графика); cmp — null без сравнения */
  buckets_cur: string[];
  buckets_cmp: string[] | null;
  cogs?: CogsView;
}

export interface SupplyIssue {
  kind: string;
  sev: string;
  text: string;
  amount?: number | null;
  supply_id?: string | number | null;
}

export interface SupplyDiag {
  supply_id: string | number | null;
  slot_date: string | null;
  cluster: string | null;
  dropoff: string | null;
  state: string | null;
  crossdock: boolean | null;
  plan_units: number | null;
  fact_units: number | null;
  cargo: Record<string, number> | null;
  issues: SupplyIssue[] | null;
  has_sku_acts: boolean;
}

export interface SupplyRow {
  legal_name: string;
  order_number: string | null;
  xd: number;
  sup: number;
  total: number;
  has_diag: boolean;
  slot_date: string | null;
  cluster: string | null;
  plan_units: number | null;
  fact_units: number | null;
  has_sku_acts: boolean;
  issues: SupplyIssue[];
  issue_count: number;
  supplies: SupplyDiag[];
}

export type SupplyBy = "invoice" | "ship";

export interface OzSupplies extends Envelope {
  by: SupplyBy;
  rows: SupplyRow[];
  /** max(built_at) в `ozon_fin_supply_diag` — момент сборки диагноза, ISO в МСК (+03:00); null — таблица пуста */
  diag_as_of: string | null;
  /** диагноз собран раньше (по дате МСК) сборки витрины Ozon или пуст — начисления и диагноз на разные даты */
  diag_stale: boolean;
  /** by=invoice: начисления xd/sup периода, не попавшие ни в одну заявку; ship — null */
  unlinked: { xd: number; sup: number; total: number } | null;
  totals: { count: number; with_issues: number; xd: number; sup: number; total: number };
}

export interface ActItem {
  legal_name: string;
  supply_id: string | number;
  act_id: string | number | null;
  act_type: string | null;
  sku: number | null;
  offer_id: string | null;
  name: string | null;
  declared: number | null;
  fact: number | null;
  approved: number | null;
  defect: number | null;
  defect_reasons: unknown;
  fact_amount: number | null;
}

export interface SupplyAccrual {
  month: string;
  legal_name: string;
  grp: string;
  grp_label: string;
  item: string;
  amount: number;
}

export interface OzSupplyDetail extends Envelope {
  order_number: string;
  legal_names: string[];
  supplies: (SupplyDiag & { legal_name: string })[];
  act_items: ActItem[];
  accruals: SupplyAccrual[];
  accrual_total: number;
}

/** Карточка артикула (`GET /product`): то же `product`, что у узла «Артикул» дерева. */
export interface MpProduct extends Envelope {
  product: TreeProduct;
  legal: string | null;
}

export interface OzProduct extends MpProduct {
  /** эхо параметра запроса (`vendor_code` — у обеих площадок) */
  vendor_code: string;
}

/** Метрики статистики рекламы Ozon за период (`GET /campaign/{id}`): null — у вида кампании
 *  такой метрики нет (у «оплаты за заказ» нет показов и кликов) или знаменатель 0 (CTR, CPC, ДРР). */
export interface CampaignStats {
  views: number | null;
  clicks: number | null;
  orders: number | null;
  /** штуки в заказах — только у «оплаты за заказ» */
  qty: number | null;
  /** выручка по атрибуции Ozon (у «оплаты за заказ» — сумма заказов) */
  revenue: number | null;
  /** расход по статистике (кабинет, с НДС), положительный */
  spend: number;
  /** строк статистики без расхода (NULL) — в сумму не вошли */
  spend_missing: number;
  ctr: number | null;
  cpc: number | null;
  drr: number | null;
}

export interface CampaignPeriod {
  /** нет таблицы статистики у кампании — null */
  stats: CampaignStats | null;
  /** корректировки Ozon (ozon_adverts_corrections), со знаком: минус — вернули часть расхода */
  corrections: number;
}

export interface CampaignSku extends CampaignStats {
  legal_name: string;
  sku: number | null;
  /** артикул из mart.mp_finance_sku_dim; нет в справочнике — null */
  offer_id: string | null;
  name: string | null;
}

export type CampaignType = "click" | "order" | "no_report";

/** Карточка рекламной кампании (`GET /campaign/{id}`): статистика Ozon. Начисления по финотчёту —
 *  `/summary?scope=obj:campaign:<id>`. */
export interface OzCampaign extends Envelope {
  campaign_id: string;
  legal_names: string[];
  /** таблица статистики: click — type_sku, order — search_promo, no_report — без отчёта; null — нет */
  type: CampaignType | null;
  type_label: string;
  stats_table: string | null;
  adv_object_types: string[];
  /** статьи витрины, по которым есть начисления кампании (за всё время) */
  accrual_items: string[];
  totals: { cur: CampaignPeriod; cmp: CampaignPeriod | null };
  /** топ-50 SKU по расходу за период; всего — sku_count */
  skus: CampaignSku[];
  sku_count: number;
  /** все дни периода (дня нет в статистике — нули); у вида без метрики — null */
  days: { date: string; spend: number; clicks: number | null; orders: number | null }[];
}

export interface ReconRow {
  month: string;
  legal_name: string;
  label: string;
  accrual: number | null;
  balance: number | null;
  /** null — баланса ЛК за месяц нет (`no_header`): разница не считается */
  diff: number | null;
  note: string | null;
  /** информационная строка (разбивка по видам) — в итоги месяца не входит */
  info?: boolean;
}

export interface ReconMonth {
  month: string;
  legal_name: string;
  accrual: number;
  /** null при `no_header` */
  balance: number | null;
  /** null при `no_header` */
  diff: number | null;
  explained: number;
  /** сумма со знаком по статьям без примечания */
  unexplained: number;
  /** сумма модулей расхождений по статьям без примечания */
  unexplained_abs: number;
  /** no_header — баланса ЛК за месяц нет (текущий месяц до первого снимка): «не с чем сверить» */
  status: "ok" | "mismatch" | "no_header";
}

export interface OzRecon extends Envelope {
  rows: ReconRow[];
  months: ReconMonth[];
  month_from: string;
  month_to: string;
  legal: string | null;
}

/** Параметры query-string: null не шлём (сравнение выключено, юрлицо — оба). */
function query(f: OzFilters): Record<string, string | boolean> {
  const p: Record<string, string | boolean> = {
    date_from: f.date_from,
    date_to: f.date_to,
    alloc: f.alloc,
  };
  if (f.cmp_from && f.cmp_to) {
    p.cmp_from = f.cmp_from;
    p.cmp_to = f.cmp_to;
  }
  if (f.legal) p.legal = f.legal;
  return p;
}

/** Повторяемый query-параметр без скобок: `scope=a&scope=b` (бэкенд — FastAPI `list[str]`). */
const REPEAT = { indexes: null } as const;

/** Площадка для клиента общих ручек: база путей и имя параметра ключа артикула в `/product`. */
export interface AnalyticsApiConfig {
  base: string;
  productParam: "offer_id" | "vendor_code";
}

/** Общие ручки отчёта (meta, summary, trend, tree, product) — одинаковые у Ozon и WB. */
export function createAnalyticsApi({ base, productParam }: AnalyticsApiConfig) {
  async function getMeta(signal?: AbortSignal): Promise<OzMeta> {
    const { data } = await api.get<OzMeta>(`${base}/meta`, { signal });
    return data;
  }

  async function getSummary(f: OzFilters, signal?: AbortSignal): Promise<OzSummary> {
    const { data } = await api.get<OzSummary>(`${base}/summary`, { params: query(f), signal });
    return data;
  }

  /** Сводка по фактам сущности: `scope` — `<dim>:<key>` (карточка артикула и т.п.). */
  async function getScopedSummary(
    f: OzFilters,
    scope: string[],
    signal?: AbortSignal,
  ): Promise<OzSummary> {
    const { data } = await api.get<OzSummary>(`${base}/summary`, {
      params: { ...query(f), scope },
      paramsSerializer: REPEAT,
      signal,
    });
    return data;
  }

  /** Тренд по фактам сущности (`scope`, как у сводки). */
  async function getScopedTrend(
    f: OzFilters,
    grain: Grain,
    scope: string[],
    signal?: AbortSignal,
  ): Promise<OzTrend> {
    const { data } = await api.get<OzTrend>(`${base}/trend`, {
      params: { ...query(f), grain, scope },
      paramsSerializer: REPEAT,
      signal,
    });
    return data;
  }

  /** Карточка артикула (ключ — offer_id у Ozon, vendor_code у WB); неизвестный — 404 с текстом. */
  async function getProduct(
    key: string,
    legal: string | null,
    signal?: AbortSignal,
  ): Promise<MpProduct> {
    const params: Record<string, string> = { [productParam]: key };
    if (legal) params.legal = legal;
    const { data } = await api.get<MpProduct>(`${base}/product`, { params, signal });
    return data;
  }

  async function getTrend(f: OzFilters, grain: Grain, signal?: AbortSignal): Promise<OzTrend> {
    const { data } = await api.get<OzTrend>(`${base}/trend`, {
      params: { ...query(f), grain },
      signal,
    });
    return data;
  }

  /** Тренд одной строки P&L: группа `grp` и, опционально, статья `item` внутри неё. */
  async function getTrendSlice(
    f: OzFilters,
    grain: Grain,
    grp: string,
    item: string | null,
    signal?: AbortSignal,
  ): Promise<OzTrend> {
    const params: Record<string, string | boolean> = { ...query(f), grain, grp };
    if (item !== null) params.item = item;
    const { data } = await api.get<OzTrend>(`${base}/trend`, { params, signal });
    return data;
  }

  async function postTree(req: TreeRequest, signal?: AbortSignal): Promise<OzTree> {
    const body: Record<string, unknown> = { ...req };
    if (!req.cmp_from || !req.cmp_to) {
      delete body.cmp_from;
      delete body.cmp_to;
    }
    if (!req.legal) delete body.legal;
    if (!req.search) delete body.search;
    if (!req.obj_kind) delete body.obj_kind;
    const { data } = await api.post<OzTree>(`${base}/tree`, body, { signal });
    return data;
  }

  return {
    getMeta,
    getSummary,
    getScopedSummary,
    getScopedTrend,
    getProduct,
    getTrend,
    getTrendSlice,
    postTree,
  };
}

export type AnalyticsApi = ReturnType<typeof createAnalyticsApi>;

/** Ozon — витрина dwh `mart.mp_finance_*` (`mp = 'ozon'`), ручки `/mp-analytics/ozon/*`. */
const BASE = "/mp-analytics/ozon";

// Ozon: общие ручки — именованными экспортами (клиент по площадке — mpAnalytics.analyticsApi)
export const {
  getMeta,
  getSummary,
  getScopedSummary,
  getScopedTrend,
  getProduct,
  getTrend,
  getTrendSlice,
  postTree,
} = createAnalyticsApi({ base: BASE, productParam: "vendor_code" });

/** Карточка рекламной кампании: статистика Ozon за период и сравнение; нет данных — 404. */
export async function getCampaign(
  campaignId: string,
  f: OzFilters,
  signal?: AbortSignal,
): Promise<OzCampaign> {
  const { data } = await api.get<OzCampaign>(
    `${BASE}/campaign/${encodeURIComponent(campaignId)}`,
    { params: query(f), signal },
  );
  return data;
}

export async function getSupplies(f: OzFilters, by: SupplyBy, signal?: AbortSignal): Promise<OzSupplies> {
  const { data } = await api.get<OzSupplies>(`${BASE}/supplies`, { params: { ...query(f), by }, signal });
  return data;
}

export async function getSupplyDetail(
  orderNumber: string,
  legal: string | null,
  alloc: boolean,
  signal?: AbortSignal,
): Promise<OzSupplyDetail> {
  const params: Record<string, string | boolean> = { alloc };
  if (legal) params.legal = legal;
  const { data } = await api.get<OzSupplyDetail>(
    `${BASE}/supplies/${encodeURIComponent(orderNumber)}`,
    { params, signal },
  );
  return data;
}

export async function getRecon(
  monthFrom: string,
  monthTo: string,
  legal: string | null,
  signal?: AbortSignal,
): Promise<OzRecon> {
  const params: Record<string, string> = { month_from: monthFrom, month_to: monthTo };
  if (legal) params.legal = legal;
  const { data } = await api.get<OzRecon>(`${BASE}/recon`, { params, signal });
  return data;
}
